/**
 * How the quiz decides what you got right — ROUTE_INVENTORY §2.11 #63 and #66.
 *
 * Two rules, both invisible on screen and both easy to get subtly wrong:
 *
 * - **Correctness is compared by ANSWER TEXT, never by index.** The options
 *   are shuffled per question (cheap anti-memorisation), so an index-based
 *   comparison marks the right answer wrong as soon as the shuffle moves it.
 *   The trim-and-lowercase is what makes the comparison survive the whitespace
 *   and casing drift that generated options actually carry.
 * - **A concept's grade comes from the RATIO of its attempts**, because a
 *   concept can carry several questions. Getting one of three right is not the
 *   same as getting one of one right, and the FSRS schedule reads this number.
 *
 * Pure, and in `src/lib`: no hooks, no React.
 */

import type { QuestionAttempt } from './session-types';

/** Normalise an answer for comparison: trimmed, case-folded. */
export function normalizeAnswer(answer: string): string {
  return answer.trim().toLowerCase();
}

/**
 * Did this pick match the correct answer?
 *
 * `undefined` picks are wrong rather than throwing — a question advanced
 * without a pick is a skipped question, which is a real state on the quiz's
 * timed path.
 */
export function isCorrectAnswer(
  picked: string | undefined | null,
  correct: string
): boolean {
  if (picked == null) return false;
  return normalizeAnswer(picked) === normalizeAnswer(correct);
}

/**
 * §2.11 #66 — one grade for a concept, from however many questions it had.
 *
 * All right is Easy, at least half is Good, anything at all is Hard, nothing
 * is Again. The thresholds matter because they feed FSRS: `>= 0.5` rather than
 * `> 0.5` means two of four counts as Good, which is the reading that keeps a
 * partially-known concept on a sensible schedule rather than punishing it.
 *
 * No attempts at all grades Good — a concept whose questions were never shown
 * must not be scheduled as a failure.
 */
export function aggregateGradeFromAttempts(
  attempts: Pick<QuestionAttempt, 'wasCorrect'>[]
): 1 | 2 | 3 | 4 {
  if (attempts.length === 0) return 3;
  const correct = attempts.filter(a => a.wasCorrect).length;
  const ratio = correct / attempts.length;
  if (ratio === 1) return 4;
  if (ratio >= 0.5) return 3;
  if (ratio > 0) return 2;
  return 1;
}

/**
 * §2.11 #68 — what the next-action button should say.
 *
 * Three states, and the label is the only thing telling the user whether
 * pressing it costs them another question, moves them to a new concept, or
 * ends the session. "Next" for all three would make finishing a surprise.
 */
export function nextActionLabel({
  questionIndex,
  questionCount,
  conceptIndex,
  conceptCount,
}: {
  questionIndex: number;
  questionCount: number;
  conceptIndex: number;
  conceptCount: number;
}): 'Next question' | 'Next concept' | 'Finish session' {
  if (questionIndex + 1 < questionCount) return 'Next question';
  if (conceptIndex + 1 < conceptCount) return 'Next concept';
  return 'Finish session';
}
